import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { RpsGuiasUrgenciaEmergencia } from '@gestor-web/@core/api/ords/model';
import { LayoutActions, OperadorasActions } from '@gestor-web/@core/store/actions';
import { LayoutSelectors, OperadorasSelectors } from '@gestor-web/@core/store/selectors';
import { filterToQueryString } from '@gestor-web/@core/utils';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { GeralService, NotificationService, PermissaoService } from '@gestor-web/app/shared/services';
import { GridService } from '@gestor-web/app/shared/services/grid.service';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { GridOptions } from 'ag-grid-enterprise';
import { AgFilterUtils, buildGridOptions, exportGridAsExcel } from '@gestor-web/app/shared/components';
import { Observable, Subject, combineLatest } from 'rxjs';
import { filter, first, map, takeUntil } from 'rxjs/operators';
import { RpsGuiasUrgenciaEmergenciaService } from './rps-guias-urgencia-emergencia.service';
import { FUNCIONALIDADE } from '@gestor-web/@core/auth/funcionalidades';

const GRID_KEY = 'ANALISES.RPS_GUIAS_URGENCIA_EMERGENCIA';

const SITUACAO_OPTIONS = [
  { label: '1 - Prestador com Urgência/Emergência e plano vinculado', value: 1 },
  { label: '2 - Prestador com Urgência/Emergência sem plano vinculado', value: 2 },
  { label: '3 - Prestador sem Urgência/Emergência e registrado no RPS', value: 3 },
  { label: '4 - Prestador sem registro no RPS', value: 4 },
];

const COLUMNS: ColDef[] = [
  {
    headerName: 'Prestador informado na Guia',
    field: 'ds_prestador',
    width: 250,
  },
  {
    headerName: 'Plano informado na Guia',
    field: 'ds_plano',
    width: 200,
  },
  {
    headerName: '022 - Origem da Guia',
    field: 'nm_id_origem_guia',
    width: 200,
  },
  {
    headerName: 'Qtde. rede direta',
    field: 'qtde_rede_direta',
    type: 'int',
    width: 150,
  },
  {
    headerName: 'Qtde. rede indireta',
    field: 'qtde_rede_indireta',
    type: 'int',
    width: 150,
  },
  {
    headerName: 'Situação no RPS',
    field: 'ds_situacao_rps',
    width: 350,
  },
];

@Component({
    templateUrl: './rps-guias-urgencia-emergencia.component.html',
    styleUrls: ['./rps-guias-urgencia-emergencia.component.scss'],
    providers: [RpsGuiasUrgenciaEmergenciaService, DecimalPipe, DatePipe],
    standalone: false
})
export class RpsGuiasUrgenciaEmergenciaComponent implements OnInit, OnDestroy {
  readonly GRID_KEY = GRID_KEY;
  readonly situacaoOptions = SITUACAO_OPTIONS;

  @ViewChild('agGrid') agGrid: AgGridAngular<RpsGuiasUrgenciaEmergencia>;

  gridOptions: GridOptions<RpsGuiasUrgenciaEmergencia>;

  pageReady$ = new Subject<void>();
  filterReady$ = new Subject<boolean>();
  firstLoad = true;
  filterChanged = true;

  podeGerenciarVisoes = false;

  operadora: string;
  operadoraNome: string;

  competenciaRange: string[];
  selectedSituacao: number[] = [];

  rowData: RpsGuiasUrgenciaEmergencia[];

  dtUltimoProcessamento: string = null;

  pageTooltip = GRID_KEY;

  fullscreen$: Observable<boolean>;
  gridHeight$: Observable<string>;

  constructor(
    private permissaoService: PermissaoService,
    private service: RpsGuiasUrgenciaEmergenciaService,
    private decimalPipe: DecimalPipe,
    private datePipe: DatePipe,
    private notificationService: NotificationService,
    private gridService: GridService,
    private geralService: GeralService,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.fullscreen$ = this.store.select(LayoutSelectors.selectFullscreen);
    this.gridHeight$ = this.fullscreen$.pipe(
      map(fullscreen => (fullscreen ? 'calc(100vh - 130px)' : 'calc(100vh - 186px)')),
    );
  }

  ngOnInit(): void {
    const ano = new Date().getFullYear();
    this.competenciaRange = [`${ano}01`, `${ano}12`];

    this.store
      .select(OperadorasSelectors.selectWorkspaceOperadora)
      .pipe(
        filter(op => !!op),
        concatLatestFrom(() => this.store.select(OperadorasSelectors.selectWorkspaceParams)),
        takeUntil(this.ngDestroyed$),
      )
      .subscribe({
        next: ([operadora, params]) => {
          this.operadora = operadora.cd_registro_ans;
          this.operadoraNome = operadora.sg_operadora;
          this.competenciaRange = [
            params?.competencia ? '' + params.competencia : null,
            params?.competencia_final ? '' + params.competencia_final : null,
          ];

          this.loadUltimoProcessamento();

          setTimeout(() => this.pageReady$.next());
        },
        error: error => this.notificationService.handleError(error),
      });

    combineLatest([this.route.queryParams, this.pageReady$])
      .pipe(first())
      .subscribe(([params, _]) => this.applyQueryFilters(params));

    combineLatest([this.filterReady$, this.pageReady$])
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(([hasFilter, _]) => {
        if (!this.firstLoad || hasFilter) {
          this.reloadData();
        }
      });

    this.permissaoService
      .checkAcessoFeature(FUNCIONALIDADE.GERENCIAR_VISOES)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(podeGerenciarVisoes => {
        this.podeGerenciarVisoes = podeGerenciarVisoes;
        this.configureGrid();
      });
  }

  private configureGrid() {
    this.gridOptions = buildGridOptions<RpsGuiasUrgenciaEmergencia>(
      {
        columnDefs: COLUMNS,
        pipes: {
          decimal: this.decimalPipe,
        },
        views: {
          gridKey: GRID_KEY,
          hide: !this.podeGerenciarVisoes,
        },
        exportExcelFn: () => this.exportExcel(),
      },
      {
        context: {
          parentComponent: this,
        },
      },
    );
  }

  applyQueryFilters(params: Params) {
    const { competencia, competencia_final, situacao, visao, sort, extraFilters } = params;
    let hasFilter = false;

    if (competencia && competencia_final) {
      this.competenciaRange = [competencia, competencia_final];
    }

    if (situacao) {
      this.selectedSituacao = String(situacao).split(',').map(Number);
    }

    if (sort || extraFilters) {
      hasFilter = true;
    }

    const filterModel = this.gridService.getDecodedFilter(extraFilters);
    setTimeout(() => this.gridService.applyVisaoInicial(GRID_KEY, this.agGrid, visao, sort, filterModel));

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });

    this.filterReady$.next(hasFilter);
  }

  reloadData() {
    if (!this.competenciaRange?.[0] || !this.competenciaRange?.[1]) {
      this.rowData = [];
      return;
    }

    if (this.firstLoad) {
      this.firstLoad = false;
      this.agGrid?.api?.setGridOption('loading', false);
      setTimeout(() => {
        this.agGrid?.api?.setGridOption('loading', true);
      });
    } else {
      this.agGrid?.api?.setGridOption('loading', true);
    }

    this.filterChanged = false;

    this.service
      .getData(
        this.operadora,
        this.competenciaRange[0],
        this.competenciaRange[1],
        this.selectedSituacao.length ? this.selectedSituacao : undefined,
      )
      .subscribe({
        next: items => {
          this.rowData = (items ?? []).map(item => ({
            ...item,
            nm_id_origem_guia: this.geralService.buildNomeDeDominio(item.id_origem_guia, 'MON_ORIGEM_GUIA'),
          }));
        },
        error: error => this.notificationService.handleError(error),
        complete: () => this.agGrid?.api?.setGridOption('loading', false),
      });
  }

  private loadUltimoProcessamento() {
    this.service
      .getUltimoProcessamento(this.operadora)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe({
        next: response => {
          const dateResponse = response?.items?.[0]?.dt_atualizacao_mon_guia;
          this.dtUltimoProcessamento = dateResponse ? this.datePipe.transform(+dateResponse, 'dd/MM/yyyy') : null;
        },
        error: () => (this.dtUltimoProcessamento = null),
      });
  }

  competenciaChanged(range: string[]) {
    this.competenciaRange = range;
    this.filterChanged = true;
    this.store.dispatch(
      OperadorasActions.changeWorkspaceParams({
        partialParams: {
          competencia: +range[0],
          competencia_final: +range[1],
        },
      }),
    );
  }

  onSituacaoChange() {
    this.filterChanged = true;
  }

  toggleFullscreen() {
    this.store.dispatch(LayoutActions.toggleFullscreen({}));
  }

  exportExcel() {
    const visao = this.gridService.getCurrentVisao(GRID_KEY);
    const visaoNome = visao ? `- Visão ${visao?.nm_visao}` : '';

    const fileNamePrefix = `rps-guias-urgencia-emergencia-${this.operadoraNome}-${this.competenciaRange[0]}-${this.competenciaRange[1]}`;

    const situacaoTexto = this.selectedSituacao.length
      ? this.situacaoOptions
          .filter(opt => this.selectedSituacao.includes(opt.value))
          .map(opt => opt.label)
          .join(', ')
      : 'Todas';

    const headers = [
      `RPS x Guias de urgência e emergência ${visaoNome}`,
      `${this.operadoraNome} - ${this.operadora}`,
      'Competência: ' + AgFilterUtils.competenciaRangeToString(this.competenciaRange),
      'Situação no RPS: ' + situacaoTexto,
    ];

    exportGridAsExcel({
      api: this.agGrid.api,
      headers,
      fileNamePrefix,
    });
  }

  onCopyUrlFilters() {
    const visao = this.gridService.getCurrentVisao(GRID_KEY);
    const sort = this.gridService.getSimpleSort(this.agGrid);
    const extraFilters = this.gridService.getEncodedFilter(this.agGrid);

    const urlFilter = {
      competencia: this.competenciaRange[0],
      competencia_final: this.competenciaRange[1],
      situacao: this.selectedSituacao.length ? this.selectedSituacao.join(',') : undefined,
      visao: visao?.cd_visao,
      sort,
      extraFilters,
    };

    const url = filterToQueryString(urlFilter);
    navigator.clipboard.writeText(url);
    this.notificationService.info('Endereço com filtros copiado');
  }

  private ngDestroyed$ = new Subject<void>();
  ngOnDestroy() {
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
  }
}
